import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Pencil, Receipt, Power } from "@phosphor-icons/react";
import { api } from "../lib/api";

const EMPTY_FORM = {
  code: "",
  discount_type: "percentage",
  discount_value: 10,
  applies_to_plans: [],
  scope: "pack",
  recurrence: "first_cycle",
  max_uses: "",
  expires_at: "",
  active: true,
};

/**
 * Admin Promo Code manager.
 *
 * Layout: list at the top with usage stats per code, "New code" button
 * opens a modal form for create/edit. Per-code "Redemptions" button
 * opens a drawer-style modal with the redemption history.
 *
 * NOTE: All discount math happens server-side. This component only
 * collects input, posts it to the admin API, and renders the response.
 *
 * Tier checkboxes are pulled LIVE from GET /api/pricing/context's
 * `credit_packs` (the current pack catalogue) instead of a hardcoded list
 * — subscriptions are retired, so credit packs are the only product, and
 * this keeps promo scoping from drifting out of sync with costing again
 * if packs are ever renamed/added/removed.
 */
export default function AdminPromos({ refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed; {} = creating new; doc = editing
  const [viewing, setViewing] = useState(null); // promo whose redemptions are shown
  const [packs, setPacks] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/billing/promos");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load promo codes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    (async () => {
      try {
        const { data } = await api.get("/pricing/context");
        setPacks(data.credit_packs || []);
      } catch (e) {
        // Non-fatal — form falls back to showing raw tier ids with no label.
      }
    })();
  }, [refreshKey]);

  const handleDelete = async (promo) => {
    if (!window.confirm(`Delete code "${promo.code}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/billing/promos/${promo.id}`);
      toast.success(`Deleted ${promo.code}.`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
    }
  };

  const handleToggle = async (promo) => {
    try {
      await api.patch(`/admin/billing/promos/${promo.id}`, {
        active: !promo.active,
      });
      toast.success(`${promo.code} ${promo.active ? "disabled" : "enabled"}.`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed.");
    }
  };

  return (
    <div data-testid="admin-promos-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl">Promo Codes</h2>
        <button
          className="btn-accent"
          onClick={() => setEditing({})}
          data-testid="admin-promos-new"
        >
          <Plus size={14} weight="bold" /> NEW CODE
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--fg-2)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          className="hairline bg-white p-8 text-center text-[var(--fg-2)]"
          data-testid="admin-promos-empty"
        >
          No promo codes yet. Click <strong>NEW CODE</strong> to create one.
        </div>
      ) : (
        <div className="hairline bg-white overflow-x-auto" data-testid="admin-promos-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
                <th className="p-3 text-left micro-label font-normal">Code</th>
                <th className="p-3 text-left micro-label font-normal">Discount</th>
                <th className="p-3 text-left micro-label font-normal">Applies to</th>
                <th className="p-3 text-left micro-label font-normal">Scope</th>
                <th className="p-3 text-left micro-label font-normal">Uses</th>
                <th className="p-3 text-left micro-label font-normal">Revenue impact</th>
                <th className="p-3 text-left micro-label font-normal">Status</th>
                <th className="p-3 text-right micro-label font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]"
                  data-testid={`promo-row-${p.code}`}
                >
                  <td className="p-3 font-mono font-bold">{p.code}</td>
                  <td className="p-3">{describeDiscount(p)}</td>
                  <td className="p-3 text-xs">
                    {p.applies_to_plans?.length
                      ? p.applies_to_plans.join(", ")
                      : "All packs"}
                  </td>
                  <td className="p-3 text-xs">
                    {p.scope === "any" ? "Subs + Packs" : p.scope}
                    {p.scope === "subscription" || p.scope === "any" ? (
                      <div className="text-[10px] text-[var(--fg-2)]">
                        {p.recurrence === "forever"
                          ? "Recurs every cycle"
                          : "First cycle only"}
                      </div>
                    ) : null}
                  </td>
                  <td className="p-3 font-mono">
                    {p.redemption_count}
                    {p.max_uses ? ` / ${p.max_uses}` : ""}
                  </td>
                  <td className="p-3 font-mono text-[var(--accent)]">
                    −R{p.total_discount_usd.toFixed(2)}
                  </td>
                  <td className="p-3">
                    <span
                      className={`chip ${p.active ? "" : "opacity-50"}`}
                      style={
                        p.active
                          ? { background: "var(--success)", color: "white" }
                          : {}
                      }
                    >
                      {p.active ? "ACTIVE" : "DISABLED"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        title={p.active ? "Disable" : "Enable"}
                        onClick={() => handleToggle(p)}
                        className="p-1.5 hover:bg-[var(--bg)] border border-transparent hover:border-[var(--border)]"
                        data-testid={`promo-toggle-${p.code}`}
                      >
                        <Power size={14} weight="bold" />
                      </button>
                      <button
                        title="View redemptions"
                        onClick={() => setViewing(p)}
                        className="p-1.5 hover:bg-[var(--bg)] border border-transparent hover:border-[var(--border)]"
                        data-testid={`promo-view-${p.code}`}
                      >
                        <Receipt size={14} weight="bold" />
                      </button>
                      <button
                        title="Edit"
                        onClick={() => setEditing(p)}
                        className="p-1.5 hover:bg-[var(--bg)] border border-transparent hover:border-[var(--border)]"
                        data-testid={`promo-edit-${p.code}`}
                      >
                        <Pencil size={14} weight="bold" />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => handleDelete(p)}
                        className="p-1.5 hover:bg-[var(--bg)] border border-transparent hover:border-[var(--border)] text-[var(--accent)]"
                        data-testid={`promo-delete-${p.code}`}
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <PromoForm
          initial={editing}
          packs={packs}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {viewing && (
        <RedemptionsModal promo={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

function describeDiscount(p) {
  if (p.grant_credits > 0) return `+${p.grant_credits} conversions`;
  if (p.discount_type === "free") return "100% FREE";
  if (p.discount_type === "percentage") return `${p.discount_value}% off`;
  if (p.discount_type === "fixed") return `R${p.discount_value} off`;
  return "—";
}

function PromoForm({ initial, packs, onCancel, onSaved }) {
  const isEdit = initial && initial.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...(initial || {}),
    max_uses: initial?.max_uses ?? "",
    expires_at: initial?.expires_at?.slice(0, 16) ?? "",
  }));
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value:
          form.discount_type === "free" ? 0 : Number(form.discount_value),
        applies_to_plans: form.applies_to_plans,
        scope: form.scope,
        recurrence: form.recurrence,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        expires_at: form.expires_at
          ? new Date(form.expires_at).toISOString()
          : null,
        active: !!form.active,
      };
      if (isEdit) {
        await api.patch(`/admin/billing/promos/${initial.id}`, payload);
        toast.success(`Updated ${payload.code}.`);
      } else {
        await api.post("/admin/billing/promos", payload);
        toast.success(`Created ${payload.code}.`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="promo-form-modal"
    >
      <div className="bg-white border-2 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[var(--border)] p-4 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">
            {isEdit ? `Edit ${initial.code}` : "New promo code"}
          </h3>
          <button
            onClick={onCancel}
            className="text-2xl leading-none"
            data-testid="promo-form-close"
          >
            ×
          </button>
        </div>
        <div className="p-6 space-y-5">
          {/* Code */}
          <div>
            <label className="micro-label block mb-1">Code</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setField("code", e.target.value.toUpperCase())}
              placeholder="LAUNCH50"
              className="w-full px-3 py-2 border border-[var(--border)] font-mono uppercase"
              data-testid="promo-form-code"
              spellCheck={false}
            />
            <p className="text-[10px] text-[var(--fg-2)] mt-1">
              Stored upper-case. User entry is case-insensitive.
            </p>
          </div>

          {/* Discount type + value */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="micro-label block mb-1">Discount type</label>
              <select
                value={form.discount_type}
                onChange={(e) => setField("discount_type", e.target.value)}
                className="w-full px-3 py-2 border border-[var(--border)]"
                data-testid="promo-form-type"
              >
                <option value="percentage">Percentage off</option>
                <option value="fixed">Fixed amount off (R)</option>
                <option value="free">100% free (bypass payment)</option>
              </select>
            </div>
            {form.discount_type !== "free" && (
              <div>
                <label className="micro-label block mb-1">
                  Value{" "}
                  {form.discount_type === "percentage" ? "(%)" : "(R ZAR)"}
                </label>
                <input
                  type="number"
                  min="0"
                  step={form.discount_type === "percentage" ? "1" : "0.01"}
                  value={form.discount_value}
                  onChange={(e) => setField("discount_value", e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--border)] font-mono"
                  data-testid="promo-form-value"
                />
              </div>
            )}
          </div>

          {/* Plans */}
          <div>
            <label className="micro-label block mb-1">Applies to packs</label>
            <div className="flex gap-2 flex-wrap">
              {packs.map((pk) => {
                const checked = form.applies_to_plans.includes(pk.id);
                return (
                  <button
                    type="button"
                    key={pk.id}
                    onClick={() =>
                      setField(
                        "applies_to_plans",
                        checked
                          ? form.applies_to_plans.filter((x) => x !== pk.id)
                          : [...form.applies_to_plans, pk.id],
                      )
                    }
                    className={`px-3 py-1.5 text-xs uppercase font-mono border ${
                      checked
                        ? "bg-black text-white border-black"
                        : "border-[var(--border)] hover:bg-[var(--bg-2)]"
                    }`}
                    data-testid={`promo-form-plan-${pk.id}`}
                  >
                    {pk.name} · R{Number(pk.price_zar).toFixed(0)}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[var(--fg-2)] mt-1">
              Leave all unchecked to apply to every pack. List is pulled live from current pricing.
            </p>
          </div>

          {/* Scope + recurrence */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="micro-label block mb-1">Scope</label>
              <select
                value={form.scope}
                onChange={(e) => setField("scope", e.target.value)}
                className="w-full px-3 py-2 border border-[var(--border)]"
                data-testid="promo-form-scope"
              >
                <option value="pack">Credit packs (all current products)</option>
                <option value="any">Any (legacy, same effect as above)</option>
                <option value="subscription">Subscriptions (retired — no active plans, kept for old codes)</option>
              </select>
            </div>
            {form.scope === "subscription" && (
              <div>
                <label className="micro-label block mb-1">Recurrence (subs)</label>
                <select
                  value={form.recurrence}
                  onChange={(e) => setField("recurrence", e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--border)]"
                  data-testid="promo-form-recurrence"
                >
                  <option value="first_cycle">First cycle only</option>
                  <option value="forever">Apply every billing cycle (forever)</option>
                </select>
              </div>
            )}
          </div>

          {/* Limits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="micro-label block mb-1">
                Max uses (blank = unlimited)
              </label>
              <input
                type="number"
                min="0"
                value={form.max_uses}
                onChange={(e) => setField("max_uses", e.target.value)}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border border-[var(--border)] font-mono"
                data-testid="promo-form-max-uses"
              />
            </div>
            <div>
              <label className="micro-label block mb-1">Expires at (optional)</label>
              <input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setField("expires_at", e.target.value)}
                className="w-full px-3 py-2 border border-[var(--border)] font-mono text-xs"
                data-testid="promo-form-expires"
              />
            </div>
          </div>

          {/* Active */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setField("active", e.target.checked)}
              className="h-4 w-4 accent-[var(--accent)]"
              data-testid="promo-form-active"
            />
            <span className="text-sm">
              Active — users can redeem this code now.
            </span>
          </label>
        </div>

        <div className="border-t border-[var(--border)] p-4 flex items-center justify-end gap-3 bg-[var(--bg-2)]">
          <button
            onClick={onCancel}
            className="btn-primary"
            data-testid="promo-form-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !form.code.trim()}
            className="btn-accent disabled:opacity-50"
            data-testid="promo-form-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create code"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RedemptionsModal({ promo, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/billing/promos/${promo.id}/redemptions`);
        setData(r.data);
      } catch (e) {
        toast.error("Could not load redemption history.");
      }
    })();
  }, [promo.id]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="promo-redemptions-modal"
    >
      <div className="bg-white border-2 border-black max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[var(--border)] p-4 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">
            Redemptions — <span className="font-mono">{promo.code}</span>
          </h3>
          <button onClick={onClose} className="text-2xl leading-none">
            ×
          </button>
        </div>
        {!data ? (
          <div className="p-6 text-sm text-[var(--fg-2)]">Loading…</div>
        ) : data.redemptions.length === 0 ? (
          <div className="p-6 text-sm text-[var(--fg-2)]">
            No redemptions yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
                  <th className="p-3 text-left micro-label font-normal">When</th>
                  <th className="p-3 text-left micro-label font-normal">User</th>
                  <th className="p-3 text-left micro-label font-normal">Kind</th>
                  <th className="p-3 text-left micro-label font-normal">Tier</th>
                  <th className="p-3 text-left micro-label font-normal">Original</th>
                  <th className="p-3 text-left micro-label font-normal">Discount</th>
                  <th className="p-3 text-left micro-label font-normal">Final</th>
                </tr>
              </thead>
              <tbody>
                {data.redemptions.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--border)]">
                    <td className="p-3 font-mono text-[10px]">
                      {new Date(r.redeemed_at).toLocaleString()}
                    </td>
                    <td className="p-3">{r.user_email}</td>
                    <td className="p-3">{r.kind}</td>
                    <td className="p-3 font-mono">
                      {r.plan_id || r.pack_id || "—"}
                    </td>
                    <td className="p-3 font-mono">
                      R{r.original_amount_usd.toFixed(2)}
                    </td>
                    <td className="p-3 font-mono text-[var(--accent)]">
                      −R{r.discount_amount_usd.toFixed(2)}
                    </td>
                    <td className="p-3 font-mono">
                      R{r.final_amount_usd.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-[var(--border)] p-4 bg-[var(--bg-2)] flex justify-end">
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
